import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { doc, getDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';

const PublicProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [slug]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      
      // Load profile
      const profileRef = doc(db, 'profiles', slug);
      const profileDoc = await getDoc(profileRef);

      if (!profileDoc.exists()) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const profileData = {
        slug: profileDoc.id,
        ...profileDoc.data()
      };
      setProfile(profileData);

      // Load links
      const linksRef = collection(db, 'profiles', slug, 'links');
      const linksQuery = query(linksRef, orderBy('order', 'asc'));
      const linksSnapshot = await getDocs(linksQuery);
      
      const linksData = linksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLinks(linksData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading profile:', error);
      setNotFound(true);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--black)',
        color: 'var(--white)'
      }}>
        <div className="loading-spinner" style={{ 
          width: '40px', 
          height: '40px',
          borderWidth: '4px'
        }}></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--black)',
        color: 'var(--white)',
        padding: 'var(--spacing-xl)',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '6rem',
          marginBottom: 'var(--spacing-lg)'
        }}>
          🔍
        </div>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '2.5rem',
          marginBottom: 'var(--spacing-md)',
          color: 'var(--white)'
        }}>
          Profile Not Found
        </h1>
        <p style={{
          fontSize: '1.1rem',
          color: 'var(--white-dim)',
          marginBottom: 'var(--spacing-xl)',
          maxWidth: '500px'
        }}>
          The profile "/{slug}" doesn't exist. It may have been deleted or the URL might be incorrect.
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: 'var(--spacing-md) var(--spacing-xl)',
            background: 'linear-gradient(135deg, var(--gold), var(--gold-bright))',
            color: 'var(--black)',
            border: 'none',
            borderRadius: 'var(--radius-lg)',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all var(--transition-base)'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--black)',
      padding: 'var(--spacing-xl) var(--spacing-md)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ maxWidth: '600px', width: '100%' }}>
        {/* Header with logo */}
        <div style={{
          textAlign: 'center',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '1.5rem',
            fontFamily: 'var(--font-display)',
            color: 'var(--gold)',
            marginBottom: 'var(--spacing-md)',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}>
            <span style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, var(--gold), var(--gold-bright))',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--black)',
              fontWeight: '900',
              fontSize: '1.5rem'
            }}>
              R
            </span>
            <span>RiseMyRank</span>
            <span style={{
              background: 'var(--gold)',
              color: 'var(--black)',
              fontSize: '0.65rem',
              padding: '3px 10px',
              borderRadius: 'var(--radius-sm)',
              fontWeight: '800',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              transform: 'translateY(-6px)',
              boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
            }}>
              .club
            </span>
          </div>
        </div>

        {/* Premium Card - Using same classes as dashboard */}
        <div 
          className={`dashboard-card-preview-premium dashboard-card-font-${profile.cardFont || 'playfair'}`}
          style={{
            background: 'linear-gradient(135deg, var(--grey-dark) 0%, #0a0a0a 100%)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-xl)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
            boxShadow: `0 0 ${60 * (profile.glowIntensity || 0.4)}px ${profile.glowColor || '#d4af37'}${Math.round((profile.glowIntensity || 0.4) * 120).toString(16).padStart(2, '0')}`,
            border: `2px solid ${profile.glowColor || '#d4af37'}${Math.round((profile.glowIntensity || 0.4) * 80).toString(16).padStart(2, '0')}`
          }}
        >
          {/* Background gradient overlay */}
          <div style={{
            content: '',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: 'linear-gradient(180deg, rgba(26, 26, 26, 0.8) 0%, transparent 100%)',
            pointerEvents: 'none',
            zIndex: 0
          }}></div>

          {/* Profile Photo */}
          <div style={{
            width: '140px',
            height: '140px',
            margin: '0 auto var(--spacing-md)',
            position: 'relative',
            zIndex: 1
          }}>
            {profile.avatarUrl ? (
              <img 
                src={profile.avatarUrl} 
                alt={profile.displayName || 'Profile'} 
                style={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid var(--grey-medium)',
                  boxShadow: 'var(--shadow-lg)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              style={{ 
                width: '140px',
                height: '140px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid var(--grey-medium)',
                boxShadow: 'var(--shadow-lg)',
                display: profile.avatarUrl ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3.5rem',
                fontWeight: '700',
                color: 'var(--black)',
                background: `linear-gradient(135deg, ${profile.glowColor || '#d4af37'}, ${profile.glowColor || '#d4af37'}dd)`
              }}
            >
              {profile.displayName ? profile.displayName.charAt(0).toUpperCase() : '👤'}
            </div>
          </div>

          {/* Name */}
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.75rem',
            fontWeight: '700',
            marginBottom: '8px',
            color: 'var(--white)',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            position: 'relative',
            zIndex: 1,
            wordBreak: 'break-word',
            lineHeight: 1.2
          }}>
            {profile.displayName || 'No Name Set'}
          </h1>

          {/* Bio */}
          {profile.bio && (
            <p style={{
              color: 'var(--white-dim)',
              marginBottom: 'var(--spacing-lg)',
              lineHeight: 1.6,
              fontSize: '0.9rem',
              position: 'relative',
              zIndex: 1
            }}>
              {profile.bio}
            </p>
          )}

          {/* Contact & Links */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            position: 'relative',
            zIndex: 1
          }}>
            {/* Contact Buttons */}
            {profile.email && (
              <a
                href={`mailto:${profile.email}`}
                style={{
                  padding: '16px 24px',
                  color: 'var(--black)',
                  textDecoration: 'none',
                  borderRadius: 'var(--radius-lg)',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  letterSpacing: '0.05em',
                  transition: 'all var(--transition-base)',
                  display: 'block',
                  textTransform: 'uppercase',
                  border: 'none',
                  cursor: 'pointer',
                  background: `linear-gradient(135deg, ${profile.glowColor || '#d4af37'}, ${profile.glowColor || '#d4af37'}dd)`,
                  boxShadow: `0 4px 16px ${profile.glowColor || '#d4af37'}40`,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                EMAIL {profile.displayName ? profile.displayName.toUpperCase().split(' ')[0] : 'ME'}
              </a>
            )}

            {profile.phone && (
              <a
                href={`tel:${profile.phone}`}
                style={{
                  padding: '16px 24px',
                  color: 'var(--black)',
                  textDecoration: 'none',
                  borderRadius: 'var(--radius-lg)',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  letterSpacing: '0.05em',
                  transition: 'all var(--transition-base)',
                  display: 'block',
                  textTransform: 'uppercase',
                  border: 'none',
                  cursor: 'pointer',
                  background: `linear-gradient(135deg, ${profile.glowColor || '#d4af37'}, ${profile.glowColor || '#d4af37'}dd)`,
                  boxShadow: `0 4px 16px ${profile.glowColor || '#d4af37'}40`,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                CALL {profile.displayName ? profile.displayName.toUpperCase().split(' ')[0] : 'ME'}
              </a>
            )}

            {/* Links */}
            {links.map(link => (
              <a
                key={link.id}
                href={link.url}
                style={{
                  padding: '16px 24px',
                  color: 'var(--black)',
                  textDecoration: 'none',
                  borderRadius: 'var(--radius-lg)',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  letterSpacing: '0.05em',
                  transition: 'all var(--transition-base)',
                  display: 'block',
                  textTransform: 'uppercase',
                  border: 'none',
                  cursor: 'pointer',
                  background: `linear-gradient(135deg, ${profile.glowColor || '#d4af37'}, ${profile.glowColor || '#d4af37'}dd)`,
                  boxShadow: `0 4px 16px ${profile.glowColor || '#d4af37'}40`,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.title.toUpperCase()}
              </a>
            ))}

            {/* Empty State */}
            {links.length === 0 && !profile.email && !profile.phone && (
              <div style={{
                color: 'var(--white-dim)',
                fontSize: '0.85rem',
                padding: 'var(--spacing-lg)',
                opacity: 0.6,
                textAlign: 'center'
              }}>
                This profile hasn't added any links yet
              </div>
            )}
          </div>

          {/* Share URL */}
          <div style={{
            marginTop: 'var(--spacing-lg)',
            paddingTop: 'var(--spacing-md)',
            borderTop: '1px solid var(--grey-medium)',
            fontSize: '0.75rem',
            color: 'var(--white-dim)',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1
          }}>
            <span style={{ display: 'block', marginBottom: '4px' }}>
              Share this link:
            </span>
            <span style={{
              display: 'block',
              color: 'var(--gold)',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.8rem'
            }}>
              risemyrank.club/{profile.slug}
            </span>
          </div>
        </div>

        {/* Footer CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: 'var(--spacing-xl)',
          padding: 'var(--spacing-lg)',
          background: 'var(--grey-darkest)',
          border: '1px solid var(--grey-medium)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <p style={{
            color: 'var(--white-dim)',
            marginBottom: 'var(--spacing-md)',
            fontSize: '0.9rem'
          }}>
            Want your own premium digital business card?
          </p>
          <button
            onClick={() => navigate('/signup')}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-lg)',
              background: 'linear-gradient(135deg, var(--gold), var(--gold-bright))',
              color: 'var(--black)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.9rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all var(--transition-base)'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Create Yours - $2.99/month
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
